import { jsx as _jsx } from "react/jsx-runtime";
/**
 * GameShell — composant root qui crée et expose le `GameRunController` aux enfants
 * via `GameShellContext`.
 *
 * Source : DEC-005 §pattern-unifié.
 *
 * Usage :
 *
 *   <GameShell
 *     config={shellConfig}
 *     adapter={myAdapter}
 *     buildStimulus={(s) => s}
 *     grader={(stim, resp) => ({ correct: resp.ok, reactionTimeMs: resp.rt })}
 *   >
 *     <PhaseRenderer
 *       intro={<MyIntro />}
 *       staircase={<MyStaircaseView />}
 *       debrief={<MyDebrief />}
 *     />
 *   </GameShell>
 *
 * Cycle de vie :
 *   - mount → `createGameRun(options)` + `controller.start()`
 *   - unmount → `controller.stop()`
 *   - `autoStartSessionId` (optionnel) → `controller.startSession(id)` post-mount
 *     (utile pour tests E2E qui veulent skipper l'intro user-driven).
 */
import { useEffect, useRef } from 'react';
import { GameShellContext } from '../hooks/context';
import { createGameRun } from '../runtime';
export function GameShell({ children, autoStartSessionId, ...options }) {
    const ctrlRef = useRef(null);
    if (ctrlRef.current === null) {
        ctrlRef.current = createGameRun(options);
    }
    const ctrl = ctrlRef.current;
    useEffect(() => {
        ctrl.start();
        if (autoStartSessionId !== undefined) {
            ctrl.startSession(autoStartSessionId);
        }
        return () => {
            ctrl.stop();
        };
        // ctrl est stable (ref) ; autoStartSessionId capturé une seule fois (intentionnel).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctrl]);
    return (_jsx(GameShellContext.Provider, { value: ctrl, children: children }));
}
//# sourceMappingURL=GameShell.js.map